import React, { useState, useEffect, useRef } from "react";
import "./MusicPlayer.css";

export default function MusicPlayer({ track, playlist, isPlaylistMode, onClose, isMinimized, setIsMinimized }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const audioRef = useRef(null);
  const shouldAutoPlayRef = useRef(false);

  const isMobile = window.innerWidth <= 768;
  const [localMinimized, setLocalMinimized] = useState(!isMobile);
  const minimized = isMinimized !== undefined ? isMinimized : localMinimized;
  const setMinimized = setIsMinimized || setLocalMinimized;

  // Détecter le changement de track depuis l'extérieur
  useEffect(() => {
    const index = playlist.findIndex(t => t.src === track.src);
    if (index !== -1 && index !== currentTrackIndex) {
      setCurrentTrackIndex(index);
      // Si ce n'est pas le mode playlist, on doit autoplayer
      if (!isPlaylistMode) {
        shouldAutoPlayRef.current = true;
      }
    }
  }, [track, playlist, isPlaylistMode]);

  // Charger et jouer la piste
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const currentTrack = playlist[currentTrackIndex];
    if (!currentTrack) return;

    // Réinitialiser l'état
    setCurrentTime(0);
    setIsPlaying(false);

    // Charger la nouvelle source
    audio.src = currentTrack.src;
    audio.load();

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      
      // Autoplay si demandé
      if (shouldAutoPlayRef.current) {
        shouldAutoPlayRef.current = false;
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch(err => {
              console.log("Autoplay error:", err);
              setIsPlaying(false);
            });
        }
      }
    };

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => handleNext();

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [currentTrackIndex, playlist]);

  const togglePlayPause = (e) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch(err => {
            console.log("Playback error:", err);
            setIsPlaying(false);
          });
      }
    }
  };

  const handleRestart = (e) => {
    e.stopPropagation();
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };

  const handleNext = (e) => {
    if (e) e.stopPropagation();
    if (currentTrackIndex < playlist.length - 1) {
      setCurrentTrackIndex(currentTrackIndex + 1);
      if (isPlaying) {
        shouldAutoPlayRef.current = true;
      }
    } else {
      setCurrentTrackIndex(0);
      setIsPlaying(false);
    }
  };

  const handleToggleMinimize = () => {
    setMinimized(!minimized);
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percentage * duration;
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const currentTrack = playlist[currentTrackIndex];
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <>
      <audio ref={audioRef} />
      <div className={`music-player ${minimized ? 'minimized' : ''}`}>
        {!minimized ? (
          <>
            <div className="music-player-content" onClick={handleToggleMinimize}>
              <div className="track-info">
                <div className="track-icon">
                  <img src="/applemusic.png" alt="Music" style={{ width: '100%', height: '100%', borderRadius: '12px', objectFit: 'cover' }} />
                </div>
                <div className="track-details">
                  <div className="track-name">{currentTrack.name}</div>
                  <div className="track-time">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </div>
                </div>
                <div className="audio-indicator">
                  <div className={`audio-bar ${isPlaying ? 'playing' : ''}`}></div>
                  <div className={`audio-bar ${isPlaying ? 'playing' : ''}`}></div>
                  <div className={`audio-bar ${isPlaying ? 'playing' : ''}`}></div>
                  <div className={`audio-bar ${isPlaying ? 'playing' : ''}`}></div>
                </div>
              </div>

              <div className="player-controls">
                <button className="control-btn" onClick={handleRestart} title="Recommencer">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 3v2a5 5 0 1 1-5 5H3a7 7 0 1 0 7-7V1L6 4.5 10 8V3z"/>
                  </svg>
                </button>

                <button className="control-btn play-pause" onClick={togglePlayPause}>
                  {isPlaying ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="4" width="4" height="16" rx="1"/>
                      <rect x="14" y="4" width="4" height="16" rx="1"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  )}
                </button>

                <button className="control-btn" onClick={handleNext} title="Suivant">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M4 4l10 6-10 6V4zm11 0v12h2V4h-2z"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="progress-bar" onClick={handleSeek}>
              <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
          </>
        ) : (
          <div className="music-player-mini" onClick={handleToggleMinimize}>
            <div className="track-info">
              <div className="track-icon">
                <img src="/applemusic.png" alt="Music" style={{ width: '100%', height: '100%', borderRadius: '6px', objectFit: 'cover' }} />
              </div>
              <div className="track-details">
                <div className="track-name">{currentTrack.name}</div>
                <div className="track-time">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>
              <div className="audio-indicator">
                <div className={`audio-bar ${isPlaying ? 'playing' : ''}`}></div>
                <div className={`audio-bar ${isPlaying ? 'playing' : ''}`}></div>
                <div className={`audio-bar ${isPlaying ? 'playing' : ''}`}></div>
                <div className={`audio-bar ${isPlaying ? 'playing' : ''}`}></div>
              </div>
            </div>

            <div className="player-controls">
              <button className="control-btn" onClick={handleRestart} title="Recommencer">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 3v2a5 5 0 1 1-5 5H3a7 7 0 1 0 7-7V1L6 4.5 10 8V3z"/>
                </svg>
              </button>

              <button className="control-btn play-pause" onClick={togglePlayPause}>
                {isPlaying ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" rx="1"/>
                    <rect x="14" y="4" width="4" height="16" rx="1"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>

              <button className="control-btn" onClick={handleNext} title="Suivant">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M4 4l10 6-10 6V4zm11 0v12h2V4h-2z"/>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
